import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Gamepad2, Maximize2 } from "lucide-react";

export interface SimulationConfig {
  type: "simulation";
  url: string;
  title?: string;
  instructions?: string;
  height?: number;
  passingScore?: number;
}

interface SimulationResult {
  score: number;
  grade?: string;
  details?: Record<string, unknown>;
}

interface SimulationLessonViewProps {
  config: SimulationConfig;
  skillNodeId: string;
  courseId?: string;
  completingLesson: boolean;
  lessonMarkedComplete: boolean;
  onComplete: () => void;
}

export default function SimulationLessonView({
  config,
  skillNodeId,
  completingLesson,
  lessonMarkedComplete,
  onComplete,
}: SimulationLessonViewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [latestResult, setLatestResult] = useState<SimulationResult | null>(null);

  useEffect(() => {
    let simOrigin: string;
    try {
      simOrigin = new URL(config.url).origin;
    } catch {
      return;
    }

    const handler = async (event: MessageEvent) => {
      if (event.origin !== simOrigin) return;
      const data = event.data;
      if (!data || data.channel !== "distilled-intuition:simulation" || data.type !== "result") return;
      if (typeof data.score !== "number") return;

      const result: SimulationResult = { score: data.score, grade: data.grade, details: data.details };
      setLatestResult(result);

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        await supabase.from("skill_node_simulation_attempts").insert({
          user_id: session.user.id,
          skill_node_id: skillNodeId,
          score: result.score,
          grade: result.grade ?? null,
          details: (result.details ?? null) as Json,
        });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [config.url, skillNodeId]);

  const canComplete = !!latestResult && (!config.passingScore || latestResult.score >= config.passingScore);

  return (
    <div className="space-y-4">
      {(config.title || config.instructions) && (
        <Card>
          <CardHeader>
            {config.title && (
              <CardTitle className="flex items-center gap-2">
                <Gamepad2 className="h-5 w-5 text-primary" />
                {config.title}
              </CardTitle>
            )}
            {config.instructions && <CardDescription>{config.instructions}</CardDescription>}
          </CardHeader>
        </Card>
      )}

      <div className="relative rounded-xl overflow-hidden border shadow-lg bg-black">
        <iframe
          ref={iframeRef}
          src={config.url}
          title={config.title || "Embedded simulation"}
          className="w-full block"
          style={{ height: config.height ?? 720 }}
          sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-fullscreen"
          allow="fullscreen; microphone"
        />
        <Button
          variant="secondary"
          size="sm"
          className="absolute top-3 right-3"
          onClick={() => iframeRef.current?.requestFullscreen?.()}
        >
          <Maximize2 className="h-4 w-4 mr-1.5" />
          Fullscreen
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center justify-between gap-3">
          {latestResult ? (
            <div className="flex items-center gap-3">
              <Badge variant={latestResult.grade === "PASS" ? "default" : "outline"}>
                {latestResult.grade ?? "Result"}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Latest attempt score: <span className="font-medium text-foreground">{latestResult.score}/100</span>
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Complete a mission in the simulation above to record your first attempt.
            </p>
          )}

          {lessonMarkedComplete ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Lesson complete
            </span>
          ) : (
            <Button onClick={onComplete} disabled={!canComplete || completingLesson}>
              {completingLesson ? "Completing…" : "Mark lesson complete"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
